import type { ReadonlyDeep, SimplifyDeep } from 'type-fest';
import { z } from 'zod';

import { RawUIFolderSchema } from './raw-ui-folder';
import { allowVariablePlaceholders } from './raw-variable-placeholder';

export type RawLocalModuleConfig = SimplifyDeep<
  ReadonlyDeep<z.infer<typeof RawLocalModuleConfigSchema>>
>;

export type RawRegistryModuleConfig = SimplifyDeep<
  ReadonlyDeep<z.infer<typeof RawRegistryModuleConfigSchema>>
>;

const createRawRegistryModuleConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      // type cannot use variable substitution because it is incompatible with z.discriminatedUnion
      type: z.literal('registry'),
      name: z.string(),
      module_id: z.string(),
      version: z.string(),
      reload_path: z.string().optional(),
      reload_enabled: z.boolean().optional(),
      reload_user: z.string().optional(),
      reload_time: z.string().optional(),
      reload_annotation: z.string().optional(),
      disabled: z.boolean().optional(),
      notes: z.string().optional(),
      ui_folder: RawUIFolderSchema.optional(),
      log_level: z.string().optional(),
    }),
    allowVariables,
    ['type'] // Only exclude type (discriminator)
  ).passthrough();
};

const createRawLocalModuleConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      // type cannot use variable substitution because it is incompatible with z.discriminatedUnion
      type: z.literal('local'),
      name: z.string(),
      executable_path: z.string(),
      disabled: z.boolean().optional(),
      notes: z.string().optional(),
      ui_folder: RawUIFolderSchema.optional(),
      log_level: z.string().optional(),
    }),
    allowVariables,
    ['type'] // Only exclude type (discriminator)
  ).passthrough();
};

export const RawRegistryModuleConfigSchema =
  createRawRegistryModuleConfigSchema();
export const RawLocalModuleConfigSchema = createRawLocalModuleConfigSchema();

export const RawModuleConfigSchema = z.discriminatedUnion('type', [
  RawRegistryModuleConfigSchema,
  RawLocalModuleConfigSchema,
]);

// Export the factory functions for use in fragment config
export const createRawModuleConfigSchema = (allowVariables = false) => {
  return z.discriminatedUnion('type', [
    createRawRegistryModuleConfigSchema(allowVariables),
    createRawLocalModuleConfigSchema(allowVariables),
  ]);
};
